import { isStringValue } from '../validation/primitives';
/**
 * The single client-side path to a Polar checkout.
 *
 * Both entry points use this: clicking a plan card, and the post-signup
 * auto-resume (`/pricing?checkout=1&plan=…`). Keeping it in one place is what
 * stops the two from drifting — the 2026-07 audit found checkout only fired
 * when `pathname === '/dashboard/pricing'`, so the identical click on `/pricing`
 * silently did something else entirely.
 *
 * Instrumentation is deliberate: `checkout_attempted` fires here, BEFORE the
 * request. `checkout_started` fires server-side only once Polar hands back a
 * session. Anything lost between the two (auth expiry, rate limit, network) now
 * shows up as `checkout_failed` instead of vanishing.
 */

import { emitClientEvent } from '@/lib/analytics/emit-client-event';

export type BillingInterval = 'monthly' | 'annual';

export interface StartCheckoutArgs {
  plan: string;
  interval: BillingInterval;
  /** Origin paywall surface (?from=), so revenue is attributable to the gate that drove it. */
  from?: string;
  /** Promo code carried from a lifecycle email (?promo=). */
  promo?: string;
  /** Pathname the click happened on — for funnel slicing. */
  page?: string;
  /**
   * Pay-as-you-go minute pack (TOS-52). When set, the server sells this
   * one-time pack instead of a plan; pass `plan: 'payg_<size>'` for analytics.
   */
  pack?: '120' | '300' | '600';
}

export type StartCheckoutResult =
  | { status: 'redirecting' }
  | { status: 'already_subscribed'; message: string; manageUrl?: string }
  | { status: 'error'; message: string };

export interface CheckoutPorts {
  emit: typeof emitClientEvent;
  fetch: typeof fetch;
  navigate: (url: string) => void;
}

export function startCheckout(args: StartCheckoutArgs): Promise<StartCheckoutResult> {
  return executeCheckout(args, {
    emit: emitClientEvent,
    fetch: (...input) => fetch(...input),
    navigate: (url) => {
      window.location.href = url;
    },
  });
}

/** Real orchestration with mockable effects; tests never call auth/Polar. */
export async function executeCheckout(
  args: StartCheckoutArgs,
  ports: CheckoutPorts
): Promise<StartCheckoutResult> {
  const { plan, interval, from, promo, page, pack } = args;

  ports.emit('/api/events/conversion', {
    name: 'checkout_attempted',
    plan,
    interval,
    from,
    page,
  });

  try {
    const response = await ports.fetch('/api/checkout/create', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        plan,
        interval,
        ...(promo ? { promo } : {}),
        ...(from ? { source: from } : {}),
        ...(pack ? { pack } : {}),
      }),
    });

    // SAFETY: The surrounding typed contract establishes the asserted representation.
    const data = await response.json().catch(() => ({}) as Record<string, unknown>);

    if (data?.success && isStringValue(data.checkoutUrl)) {
      ports.navigate(data.checkoutUrl);

      return { status: 'redirecting' };
    }

    if (response.status === 409 && data?.code === 'ALREADY_SUBSCRIBED') {
      // Not a failure — the user already has this plan. Don't log it as one.
      // SAFETY: The surrounding typed contract establishes the asserted representation.
      return {
        status: 'already_subscribed',
        message: (data.error as string) || 'You already have an active plan.',
        manageUrl: data.manageUrl as string | undefined,
      };
    }

    ports.emit('/api/events/conversion', {
      name: 'checkout_failed',
      plan,
      interval,
      status: response.status,
      code: isStringValue(data?.code) ? data.code : undefined,
      page,
    });

    // SAFETY: The surrounding typed contract establishes the asserted representation.
    return {
      status: 'error',
      message: (data?.error as string) || 'Could not start checkout. Please try again.',
    };
  } catch {
    ports.emit('/api/events/conversion', {
      name: 'checkout_failed',
      plan,
      interval,
      status: 0, // network / aborted — never reached the server
      page,
    });

    return { status: 'error', message: 'Could not reach checkout. Please try again.' };
  }
}
